import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { timerConfigAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Activity {
  id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
}

interface Disposition {
  id: number;
  name: string;
  is_active: boolean;
}

const TimerConfiguration: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [dispositions, setDispositions] = useState<Disposition[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Activity dialog
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityEditId, setActivityEditId] = useState<number | null>(null);
  const [activityName, setActivityName] = useState('');
  const [activitySortOrder, setActivitySortOrder] = useState('');
  const [activityIsActive, setActivityIsActive] = useState(true);
  const [activitySaving, setActivitySaving] = useState(false);

  // Disposition dialog
  const [dispositionDialogOpen, setDispositionDialogOpen] = useState(false);
  const [dispositionEditId, setDispositionEditId] = useState<number | null>(null);
  const [dispositionName, setDispositionName] = useState('');
  const [dispositionIsActive, setDispositionIsActive] = useState(true);
  const [dispositionSaving, setDispositionSaving] = useState(false);

  const isPmpOrSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some((r: any) => r.slug === 'pmp_admin' || r.slug === 'site_admin');
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [actRes, dispRes] = await Promise.all([
        timerConfigAPI.getActivities(),
        timerConfigAPI.getDispositions(),
      ]);
      if (actRes.data.success) setActivities(actRes.data.data || []);
      if (dispRes.data.success) setDispositions(dispRes.data.data || []);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to load configuration', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPmpOrSiteAdmin()) {
      setSnackbar({ open: true, message: 'Only PMP admins or Site admins can access timer configuration', severity: 'error' });
      return;
    }
    loadData();
  }, []);

  const openAddActivity = () => {
    setActivityEditId(null);
    setActivityName('');
    setActivitySortOrder('');
    setActivityIsActive(true);
    setActivityDialogOpen(true);
  };

  const openEditActivity = (a: Activity) => {
    setActivityEditId(a.id);
    setActivityName(a.name);
    setActivitySortOrder(String(a.sort_order));
    setActivityIsActive(a.is_active);
    setActivityDialogOpen(true);
  };

  const handleSaveActivity = async () => {
    const name = activityName.trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    try {
      setActivitySaving(true);
      if (activityEditId != null) {
        await timerConfigAPI.updateActivity(activityEditId, {
          name,
          sort_order: activitySortOrder === '' ? undefined : parseInt(activitySortOrder, 10),
          is_active: activityIsActive,
        });
        setSnackbar({ open: true, message: 'Activity updated', severity: 'success' });
      } else {
        await timerConfigAPI.createActivity({
          name,
          sort_order: activitySortOrder === '' ? undefined : parseInt(activitySortOrder, 10),
          is_active: activityIsActive,
        });
        setSnackbar({ open: true, message: 'Activity added', severity: 'success' });
      }
      setActivityDialogOpen(false);
      await loadData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to save activity', severity: 'error' });
    } finally {
      setActivitySaving(false);
    }
  };

  const handleDeleteActivity = async (id: number) => {
    if (!window.confirm('Deactivate this activity? It will no longer appear in timer dropdowns.')) return;
    try {
      await timerConfigAPI.deleteActivity(id);
      setSnackbar({ open: true, message: 'Activity deactivated', severity: 'success' });
      await loadData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to deactivate', severity: 'error' });
    }
  };

  const openAddDisposition = () => {
    setDispositionEditId(null);
    setDispositionName('');
    setDispositionIsActive(true);
    setDispositionDialogOpen(true);
  };

  const openEditDisposition = (d: Disposition) => {
    setDispositionEditId(d.id);
    setDispositionName(d.name);
    setDispositionIsActive(d.is_active);
    setDispositionDialogOpen(true);
  };

  const handleSaveDisposition = async () => {
    const name = dispositionName.trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Name is required', severity: 'error' });
      return;
    }
    try {
      setDispositionSaving(true);
      if (dispositionEditId != null) {
        await timerConfigAPI.updateDisposition(dispositionEditId, { name, is_active: dispositionIsActive });
        setSnackbar({ open: true, message: 'Disposition updated', severity: 'success' });
      } else {
        await timerConfigAPI.createDisposition({ name, is_active: dispositionIsActive });
        setSnackbar({ open: true, message: 'Disposition added', severity: 'success' });
      }
      setDispositionDialogOpen(false);
      await loadData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to save disposition', severity: 'error' });
    } finally {
      setDispositionSaving(false);
    }
  };

  const handleDeleteDisposition = async (id: number) => {
    if (!window.confirm('Deactivate this disposition? It will no longer appear in timer dropdowns.')) return;
    try {
      await timerConfigAPI.deleteDisposition(id);
      setSnackbar({ open: true, message: 'Disposition deactivated', severity: 'success' });
      await loadData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.message || 'Failed to deactivate', severity: 'error' });
    }
  };

  if (!isPmpOrSiteAdmin()) {
    return (
      <Container maxWidth="md">
        <Paper sx={{ p: 3, mt: 3 }}>
          <Alert severity="error">Only PMP admins or Site admins can access timer configuration.</Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h5" gutterBottom>
        Timer Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage timer disposition types and timer activity types used when starting/stopping timers on cases.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Disposition Types" />
        <Tab label="Timer Activities" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Disposition Types</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={openAddDisposition}>
                  Add disposition
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dispositions.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell>{d.id}</TableCell>
                        <TableCell>{d.name}</TableCell>
                        <TableCell>{d.is_active ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditDisposition(d)} aria-label="Edit">
                            <Edit />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteDisposition(d.id)} aria-label="Deactivate" color="error">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {dispositions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No dispositions. Add one to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {tab === 1 && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6">Timer Activities</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Deleting an activity only deactivates it; the record stays in the database for history.
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={openAddActivity}>
                  Add activity
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Sort order</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activities.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.id}</TableCell>
                        <TableCell>{a.name}</TableCell>
                        <TableCell>{a.sort_order}</TableCell>
                        <TableCell>{a.is_active ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditActivity(a)} aria-label="Edit">
                            <Edit />
                          </IconButton>
                          <Tooltip title="Deactivate (soft delete – keeps record in database)">
                            <IconButton size="small" onClick={() => handleDeleteActivity(a.id)} aria-label="Deactivate" color="error">
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {activities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          No activities. Add one to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      {/* Activity add/edit dialog */}
      <Dialog open={activityDialogOpen} onClose={() => setActivityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{activityEditId != null ? 'Edit Activity' : 'Add Activity'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={activityName}
            onChange={(e) => setActivityName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Sort order"
            type="number"
            fullWidth
            value={activitySortOrder}
            onChange={(e) => setActivitySortOrder(e.target.value)}
            inputProps={{ min: 0 }}
          />
          <FormControlLabel
            control={<Switch checked={activityIsActive} onChange={(e) => setActivityIsActive(e.target.checked)} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveActivity} variant="contained" disabled={activitySaving}>
            {activitySaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disposition add/edit dialog */}
      <Dialog open={dispositionDialogOpen} onClose={() => setDispositionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dispositionEditId != null ? 'Edit Disposition' : 'Add Disposition'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={dispositionName}
            onChange={(e) => setDispositionName(e.target.value)}
          />
          <FormControlLabel
            control={<Switch checked={dispositionIsActive} onChange={(e) => setDispositionIsActive(e.target.checked)} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispositionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveDisposition} variant="contained" disabled={dispositionSaving}>
            {dispositionSaving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TimerConfiguration;
